import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IoMdEye, IoIosDocument, IoMdDownload } from 'react-icons/io';
import PageForm from '../../templates/PageForm';
import SelectFilter from '../../organisms/SelectFilter';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';

const API_KEY = process.env.REACT_APP_API_KEY || '';
const DATA_PROVIDER_BASE_URL = `${process.env.REACT_APP_ROUTER_API_URL}/data-provider` || '';
const RECOMMENDATION_ENGINE_BASE_URL = `${process.env.REACT_APP_ROUTER_API_URL}/recommendation-engine` || '';

interface BatchOption {
    sk: string;
    identification: string;
    id: string;
    documentsCount: string;
    provider: string;
}

interface DocumentPLDAnalysis {
    document: string;
    entityName: string;
    entityType: 'CPF' | 'CNPJ';
    providers: {
        INOVAMIND: { recommendation: string; reasons: string[]; batchId: string } | null;
        ESCAVADOR: { recommendation: string; reasons: string[]; batchId: string } | null;
        QUOD: { recommendation: string; reasons: string[]; batchId: string } | null;
    };
    finalRecommendation: 'APPROVED' | 'REJECTED' | '';
    riskLevel?: 'BAIXO' | 'MEDIO' | 'ALTO' | '';
    creditFlag?: boolean;
    observations: string;
}

const RecommendationEngineParecerView: React.FC = () => {
    const navigate = useNavigate();
    const { batchSk } = useParams<{ batchSk: string }>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Estado do parecer salvo
    const [savedReport, setSavedReport] = useState<{id: string, savedAt: string} | null>(null);

    // Dados dos selects
    const [batchOptions, setBatchOptions] = useState<BatchOption[]>([]);
    const [selectedBatches, setSelectedBatches] = useState({
        INOVAMIND: '',
        ESCAVADOR: '',
        QUOD: ''
    });

    // Dados dos documentos
    const [documents, setDocuments] = useState<DocumentPLDAnalysis[]>([]);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [selectedDocumentRules, setSelectedDocumentRules] = useState<string[]>([]);

    useEffect(() => {
        loadBatchOptions();
    }, []);

    const loadBatchOptions = async () => {
        try {
            // Usar BFF service em vez de chamada direta
            const data = await recommendationBFFService.fetchBatchList();
            
            // Filtrar apenas lotes dos providers PLD e status COMPLETED
            const pldProviders = ['INOVAMIND', 'ESCAVADOR', 'QUOD-PJ'];
            const filteredBatches = data.list.filter((batch: any) =>
                pldProviders.includes(batch.provider) && batch.status === 'COMPLETED'
            );

            setBatchOptions(filteredBatches);

        } catch (error) {
            console.error('Erro ao carregar lotes:', error);
            setError('Erro ao carregar lista de lotes');
        }
    };

    const handleBatchSelection = (provider: string, batchSk: string) => {
        setSelectedBatches(prev => ({
            ...prev,
            [provider]: batchSk
        }));
    };

    const clearFilters = () => {
        setSelectedBatches({
            INOVAMIND: '',
            ESCAVADOR: '',
            QUOD: ''
        });
        setDocuments([]);
        setSavedReport(null); // Limpar estado do parecer salvo
    };

    const loadDocuments = async () => {
        const selectedCount = Object.values(selectedBatches).filter(sk => sk).length;
        if (selectedCount === 0) {
            toast.warning('Selecione pelo menos um lote para carregar os documentos');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const allDocuments = new Map<string, DocumentPLDAnalysis>();

            // Carregar dados de cada lote selecionado
            for (const [provider, batchSk] of Object.entries(selectedBatches)) {
                if (!batchSk) continue;

                const normalizedProvider = provider === 'QUOD' ? 'QUOD-PJ' : provider;

                try {
                    // Usar BFF service em vez de chamada direta
                    const batchData = await recommendationBFFService.analyzeBatch(batchSk);

                    // Enriquecer com recomendações PLD
                    await enrichWithPLDRecommendations(batchData, provider);

                    // Processar documentos do lote
                    if (batchData.reports && Array.isArray(batchData.reports)) {
                        const documentMap = new Map<string, any>();

                        // Agrupar por documento (pegar o melhor nome)
                        batchData.reports.forEach((report: any) => {
                            const document = report.document;
                            if (!documentMap.has(document)) {
                                documentMap.set(document, report);
                            } else {
                                const existing = documentMap.get(document);
                                const existingHasName = existing.name?.trim() || existing.nome?.trim();
                                const currentHasName = report.name?.trim() || report.nome?.trim();

                                if (currentHasName && !existingHasName) {
                                    documentMap.set(document, report);
                                }
                            }
                        });

                        // Adicionar/atualizar documentos no mapa geral
                        documentMap.forEach((report) => {
                            const document = report.document;
                            // Usar document_type do backend ou fallback para lógica local
                            const entityType = report.backend_document_type ||
                                (document.includes('.') && document.includes('-') ? 'CPF' : 'CNPJ');

                            if (!allDocuments.has(document)) {
                                allDocuments.set(document, {
                                    document,
                                    entityName: '',
                                    entityType,
                                    providers: {
                                        INOVAMIND: null,
                                        ESCAVADOR: null,
                                        QUOD: null
                                    },
                                    finalRecommendation: '',
                                    creditFlag: false,
                                    observations: ''
                                });
                            }

                            const docAnalysis = allDocuments.get(document)!;

                            // Atualizar tipo se vier do backend
                            if (report.backend_document_type) {
                                docAnalysis.entityType = report.backend_document_type;
                            }

                            // Atualizar dados do provider
                            const providerKey = provider === 'QUOD-PJ' ? 'QUOD' : provider as keyof typeof docAnalysis.providers;

                            let recommendation = 'PENDING';
                            let reasons: string[] = [];

                            if (report.backend_recommendation) {
                                switch (report.backend_recommendation.toUpperCase()) {
                                    case 'APPROVED':
                                    case 'APROVADO':
                                        recommendation = 'APPROVED';
                                        break;
                                    case 'REJECTED':
                                    case 'REJEITADO':
                                    case 'REPROVADO':
                                        recommendation = 'REJECTED';
                                        break;
                                    default:
                                        recommendation = 'PENDING';
                                }

                                if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
                                    reasons = report.backend_reasons;
                                }
                            }

                            docAnalysis.providers[providerKey] = {
                                recommendation,
                                reasons,
                                batchId: batchSk
                            };

                            // Atualizar nome com prioridade: INOVAMIND > ESCAVADOR > QUOD
                            const currentName = report.name?.trim() || report.nome?.trim() || '';
                            if (currentName) {
                                if (providerKey === 'INOVAMIND') {
                                    // INOVAMIND tem prioridade máxima
                                    docAnalysis.entityName = currentName;
                                } else if (providerKey === 'ESCAVADOR' && !docAnalysis.entityName) {
                                    // ESCAVADOR só se não tiver nome ainda
                                    docAnalysis.entityName = currentName;
                                } else if (providerKey === 'QUOD' && !docAnalysis.entityName) {
                                    // QUOD só se não tiver nome ainda
                                    docAnalysis.entityName = currentName;
                                }
                            }
                        });
                    }

                } catch (error) {
                    console.warn(`Erro ao carregar lote ${batchSk} do ${provider}:`, error);
                }
            }

            setDocuments(Array.from(allDocuments.values()));

            // Verificar se já existe parecer salvo para esta combinação de lotes
            await checkExistingParecer();

        } catch (error) {
            console.error('Erro ao carregar documentos:', error);
            setError('Erro ao carregar documentos dos lotes');
        } finally {
            setLoading(false);
        }
    };

    const checkExistingParecer = async () => {
        try {
            // Criar identificador único baseado nos lotes selecionados
            const batchKey = Object.entries(selectedBatches)
                .filter(([_, sk]) => sk)
                .sort()
                .map(([provider, sk]) => `${provider}:${sk}`)
                .join('|');

            if (!batchKey) return;

            // Verificar se existe parecer salvo no backend
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${RECOMMENDATION_ENGINE_BASE_URL}/pld-reports/check`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    batchSelections: selectedBatches
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.exists) {
                    setSavedReport({
                        id: result.report_id,
                        savedAt: new Date(result.saved_at).toLocaleString('pt-BR')
                    });
                }
            }
        } catch (error) {
            console.warn('Erro ao verificar parecer existente:', error);
            // Não mostrar erro para o usuário, é apenas uma verificação
        }
    };

    const enrichWithPLDRecommendations = async (batchData: any, provider: string) => {
        try {
            if (batchData.reports && Array.isArray(batchData.reports)) {

                for (const report of batchData.reports) {
                    // Aplicar regras PLD via recommendation-engine
                    const analysis = await applyPLDRulesDirectly(report, provider, batchData);

                    // Usar recomendação do recommendation-engine (não simplificar)
                    report.backend_recommendation = analysis.recommendation || 'APROVADO';
                    report.backend_reasons = analysis.reasons || [];
                    report.backend_risk_score = analysis.risk_score || 0.1;
                    report.backend_document_type = report.document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ';
                }
            }
        } catch (error) {
            console.error('Erro ao enriquecer com recomendações PLD:', error);
        }
    };

    // Função para aplicar regras PLD dinâmicas via recommendation-engine
    const applyPLDRulesDirectly = async (report: any, provider: string, batchData: any): Promise<any> => {
        try {
            // Construir provider_data a partir dos dados do report
            const providerData: Record<string, any> = {};
            
            // Os dados do provider estão diretamente no report (não em report.report_data)
            // Extrair apenas os campos relevantes (excluir metadados)
            const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt', 
                                   'backend_recommendation', 'backend_reasons', 'backend_risk_score', 
                                   'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
                                   'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
            
            const reportData: any = {};
            Object.keys(report).forEach(key => {
                if (!excludeFields.includes(key)) {
                    reportData[key] = report[key];
                }
            });
            
            const providerName = batchData.provider || provider;
            providerData[providerName] = {
                status: 'SUCCESS',
                data: reportData
            };
            
            console.log(`✅ Parecer: Usando dados do provider ${providerName}`);
            console.log(`📋 Parecer: Campos enviados:`, Object.keys(reportData));
            
            // Enviar para recommendation-engine usando dados do lote
            const documentType = report.document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ';
            const analysis = await recommendationBFFService.sendToRecommendationEngine({
                social_id: report.document,
                document_type: documentType as 'CPF' | 'CNPJ',
                business_area: 'PLD',
                provider_data: providerData
            });
            
            console.log(`✅ Parecer: Recomendação: ${analysis.recommendation}`);
            console.log(`✅ Parecer: Regras aplicadas: ${analysis.rules_applied?.length || 0}`);
            
            // Retornar objeto completo com recomendação, regras e risk_score
            return {
                recommendation: analysis.recommendation,
                reasons: analysis.rules_applied || [],
                risk_score: analysis.risk_score || 0.1
            };
        } catch (error) {
            console.error('Erro ao aplicar regras via recommendation-engine, usando fallback:', error);
            const fallbackReasons = applyPLDRulesDirectlyFallback(report, provider);
            return {
                recommendation: fallbackReasons.length > 0 ? 'REPROVADO' : 'APROVADO',
                reasons: fallbackReasons,
                risk_score: fallbackReasons.length > 0 ? 0.9 : 0.1
            };
        }
    };

    // Função de fallback com regras hardcoded (mantida para compatibilidade)
    const applyPLDRulesDirectlyFallback = (report: any, provider: string): string[] => {
        const reasons: string[] = [];

        if (provider === 'INOVAMIND') {
            // Aplicar regras do INOVAMIND baseadas nos dados reais
            if (report.bacen_qgi_cpf === true) {
                reasons.push('Quadro Geral de Inabilitados do Banco Central');
            }
            if (report.work_slave_cnpj === true || report.work_slave_cpf === true) {
                reasons.push('Trabalho Escravo');
            }
            if (report.criminal_record_federal_police === true) {
                reasons.push('Antecedentes Criminais (PF)');
            }
            if (report.sanctions_list === true) {
                reasons.push('Sanções OFAC');
            }
            if (report.eu_sanction_list === true) {
                reasons.push('Sanções UE');
            }
            if (report.fr_sanction_list === true) {
                reasons.push('Sanções França');
            }
            if (report.uk_sanction_list === true) {
                reasons.push('Sanções UK');
            }
            if (report.onu_sanction_lists === true) {
                reasons.push('Sanções ONU');
            }
            if (report.us_nonproliferation_sanction_list === true) {
                reasons.push('Sanções EUA');
            }
            if (report.world_bank_ineligible_sanction_list === true) {
                reasons.push('Banco Mundial');
            }
            if (report.red_notice_interpol === true) {
                reasons.push('Red Notice Interpol');
            }
            if (report.pep_search_v2 === true) {
                reasons.push('Pessoa Exposta Politicamente (PEP)');
            }
            if (report.ceis_infos_government_base_cnpj === true || report.ceis_infos_government_base_cpf === true) {
                reasons.push('CEIS');
            }
            if (report.cnep_infos_government_base_cnpj === true) {
                reasons.push('CNEP');
            }
            if (report.ceaf_infos_government_base === true) {
                reasons.push('CEAF');
            }
            if (report.ibama_assessment_cnpj === true || report.ibama_assessment_cpf === true) {
                reasons.push('IBAMA Multas');
            }
            if (report.ibama_embargo_cnpj === true || report.ibama_embargo_cpf === true) {
                reasons.push('IBAMA Embargos');
            }
        }

        else if (provider === 'ESCAVADOR') {
            // Aplicar regras do ESCAVADOR baseadas nos processos
            if (report.processos && Array.isArray(report.processos)) {
                const assuntos = report.processos.map((p: any) => p.sigla_assunto || '');

                if (assuntos.includes('CLP')) {
                    reasons.push('Crimes contra a liberdade pessoal');
                }
                if (assuntos.includes('RCAE')) {
                    reasons.push('Redução a condição análoga à de escravo');
                }
                if (assuntos.includes('RJF')) {
                    reasons.push('Recuperação Judicial/Falência');
                }
                if (assuntos.includes('DUP')) {
                    reasons.push('Duplicata');
                }
                if (assuntos.includes('COT')) {
                    reasons.push('Crimes contra a Ordem Tributária');
                }
            }
        }

        else if (provider === 'QUOD' || provider === 'QUOD-PJ') {
            // Aplicar regras do QUOD
            if (report.CompanyStatus && report.CompanyStatus !== 'ATIVO') {
                reasons.push(`Status RF: ${report.CompanyStatus}`);
            }

            // Verificar CNAE na blocklist
            const blockedCnaes = ['6201500']; // Exemplo
            if (report.MainActivity && blockedCnaes.includes(report.MainActivity)) {
                reasons.push(`CNAE bloqueado: ${report.MainActivity}`);
            }

            // Verificar pendências de crédito
            if (report.pendenciesControlCred && parseFloat(report.pendenciesControlCred) > 5000) {
                reasons.push(`Pendências de crédito: R$ ${parseFloat(report.pendenciesControlCred).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
            }

            // Verificar sócios com restrição
            if (report.partnerRestriction === true) {
                reasons.push('Sócio com restrição');
            }
        }

        // Se não há regras acionadas, não adicionar "Nenhuma restrição PLD encontrada"
        // Isso será tratado na exibição do modal

        return reasons;
    };

    const updateFinalRecommendation = (index: number, recommendation: 'APPROVED' | 'REJECTED' | '') => {
        const updatedDocuments = [...documents];
        updatedDocuments[index].finalRecommendation = recommendation;
        setDocuments(updatedDocuments);
    };

    const updateRiskLevel = (index: number, riskLevel: 'BAIXO' | 'MEDIO' | 'ALTO' | '') => {
        const updatedDocuments = [...documents];
        updatedDocuments[index].riskLevel = riskLevel;
        setDocuments(updatedDocuments);
    };

    const updateCreditFlag = (index: number, creditFlag: boolean) => {
        setDocuments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], creditFlag };
            return updated;
        });
    };

    const updateObservations = (index: number, observations: string) => {
        const updatedDocuments = [...documents];
        updatedDocuments[index].observations = observations;
        setDocuments(updatedDocuments);
    };

    const getRiskLevelBadge = (riskLevel?: string) => {
        if (!riskLevel) return null;

        const styles = {
            'BAIXO': 'bg-green-100 text-green-800 border-green-300',
            'MEDIO': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'ALTO': 'bg-red-100 text-red-800 border-red-300'
        };

        const labels = {
            'BAIXO': 'Baixo',
            'MEDIO': 'Médio',
            'ALTO': 'Alto'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[riskLevel as keyof typeof styles]}`}>
                {labels[riskLevel as keyof typeof labels]}
            </span>
        );
    };

    const showRules = (doc: DocumentPLDAnalysis) => {
        const allRules: string[] = [];

        Object.entries(doc.providers).forEach(([provider, data]) => {
            if (data && data.reasons.length > 0) {
                // Filtrar "Nenhuma restrição PLD encontrada"
                const validReasons = data.reasons.filter(reason =>
                    reason !== 'Nenhuma restrição PLD encontrada'
                );

                if (validReasons.length > 0) {
                    validReasons.forEach(reason => {
                        allRules.push(`${provider}: ${reason}`);
                    });
                } else {
                    // Só mostrar "Nenhuma restrição" se não há regras válidas
                    allRules.push(`${provider}: Nenhuma restrição PLD encontrada`);
                }
            } else {
                // Provider sem dados
                allRules.push(`${provider}: Nenhuma restrição PLD encontrada`);
            }
        });

        setSelectedDocumentRules(allRules);
        setShowRulesModal(true);
    };

    const getRecommendationBadge = (recommendation: string | null) => {
        if (!recommendation || recommendation === 'PENDING') {
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pendente</span>;
        }

        const badges: Record<string, { className: string; label: string }> = {
            'APPROVED': { className: 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Aprovado' },
            'REJECTED': { className: 'px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Reprovado' }
        };

        const badge = badges[recommendation] || badges['APPROVED'];
        return <span className={badge.className}>{badge.label}</span>;
    };

    const translateRecommendation = (recommendation: string): string => {
        const translations: Record<string, string> = {
            'APPROVED': 'Aprovado',
            'REJECTED': 'Reprovado',
            'PENDING': 'Pendente'
        };
        return translations[recommendation] || recommendation;
    };

    const translateRiskLevel = (riskLevel: string): string => {
        const translations: Record<string, string> = {
            'BAIXO': 'Baixo',
            'MEDIO': 'Médio',
            'ALTO': 'Alto'
        };
        return translations[riskLevel] || riskLevel;
    };

    const exportToCSV = () => {
        if (documents.length === 0) {
            toast.warning('Nenhum documento para exportar');
            return;
        }

        const headers = [
            'Documento',
            'Nome',
            'Tipo',
            'INOVAMIND',
            'ESCAVADOR',
            'QUOD',
            'Recomendação Final',
            'Nível de Risco',
            'Observações',
            'Regras Acionadas'
        ];

        const csvData = documents.map(doc => [
            doc.document,
            doc.entityName,
            doc.entityType === 'CPF' ? 'PF' : 'PJ',
            doc.providers.INOVAMIND ? translateRecommendation(doc.providers.INOVAMIND.recommendation) : '-',
            doc.providers.ESCAVADOR ? translateRecommendation(doc.providers.ESCAVADOR.recommendation) : '-',
            doc.providers.QUOD ? translateRecommendation(doc.providers.QUOD.recommendation) : '-',
            doc.finalRecommendation ? translateRecommendation(doc.finalRecommendation) : 'Pendente',
            doc.riskLevel ? translateRiskLevel(doc.riskLevel) : 'Não informado',
            doc.observations || '',
            getAllRulesText(doc)
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `parecer-pld-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getAllRulesText = (doc: DocumentPLDAnalysis): string => {
        const allRules: string[] = [];

        Object.entries(doc.providers).forEach(([provider, data]) => {
            if (data && data.reasons.length > 0) {
                data.reasons.forEach(reason => {
                    allRules.push(`${provider}: ${reason}`);
                });
            }
        });

        return allRules.join('; ');
    };

    const saveParecer = async () => {
        // Validar se todos os documentos têm recomendação final E nível de risco
        const missingRecommendations = documents.filter(doc => !doc.finalRecommendation);
        if (missingRecommendations.length > 0) {
            toast.error('Todos os documentos devem ter uma recomendação final');
            return;
        }

        const missingRiskLevel = documents.filter(doc => !doc.riskLevel);
        if (missingRiskLevel.length > 0) {
            toast.error('Todos os documentos devem ter um nível de risco');
            return;
        }

        // Validar se todos os documentos têm pelo menos uma recomendação de provider
        const missingProviderRecommendations = documents.filter(doc =>
            !doc.providers.INOVAMIND && !doc.providers.ESCAVADOR && !doc.providers.QUOD
        );
        if (missingProviderRecommendations.length > 0) {
            toast.error('Todos os documentos devem ter pelo menos uma recomendação de provider');
            return;
        }

        try {
            setSaving(true);

            const parecerData = {
                batchSelections: selectedBatches,
                documents: documents,
                createdAt: new Date().toISOString()
            };

            // Chamada para salvar no backend
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${RECOMMENDATION_ENGINE_BASE_URL}/pld-reports`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(parecerData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Parecer salvo:', result);

            // Salvar informações do parecer salvo (sem ID)
            const savedAt = new Date().toLocaleString('pt-BR');
            setSavedReport({
                id: result.report_id,
                savedAt: savedAt
            });

            // Não mostrar toast, apenas atualizar estado para exibir mensagem inline
        } catch (error) {
            console.error('Erro ao salvar parecer:', error);
            toast.error('Erro ao salvar parecer PLD');
        } finally {
            setSaving(false);
        }
    };

    const getProviderOptions = (provider: string) => {
        const normalizedProvider = provider === 'QUOD' ? 'QUOD-PJ' : provider;
        return batchOptions.filter(batch => batch.provider === normalizedProvider);
    };

    return (
        <PageForm
            pageTitle="Parecer PLD - Análise Multi-Provider"
            apiVersion="Recommendation Engine - 1.0.0"
            isLoading={loading}
            breadcrumb={[
                { label: 'Pareceres PLD', url: '/recommendation-engine/pareceres' },
                { label: 'Novo Parecer', url: '#' }
            ]}
            controlSlot={<div></div>}
        >
            {/* Seleção de Lotes */}
            <div className="mb-6 bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Seleção de Lotes por Provider
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {['INOVAMIND', 'ESCAVADOR', 'QUOD'].map(provider => (
                        <SelectFilter
                            key={provider}
                            id={`select-${provider}`}
                            label={provider}
                            value={selectedBatches[provider as keyof typeof selectedBatches]}
                            onChange={(value) => handleBatchSelection(provider, value)}
                            defaultVoidOption={true}
                            defaultLabel="Selecione um lote..."
                            options={getProviderOptions(provider).map(batch => ({
                                value: batch.sk,
                                label: `${batch.identification} - ${batch.id} (${batch.documentsCount} docs)`
                            }))}
                            className="w-full"
                        />
                    ))}
                </div>

                <div className="flex justify-between items-center">
                    <div className="flex gap-2 items-center">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            onClick={loadDocuments}
                            disabled={loading}
                        >
                            {loading ? 'Carregando...' : 'Carregar Documentos'}
                        </button>

                        <button
                            className="text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
                            onClick={exportToCSV}
                            disabled={documents.length === 0 || !savedReport}
                            title={!savedReport ? "Salve o parecer antes de exportar" : ""}
                        >
                            <IoMdDownload />
                            Exportar CSV
                        </button>

                        <button
                            className="text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={saveParecer}
                            disabled={saving || documents.length === 0 || savedReport !== null}
                            title={savedReport ? "Parecer já foi salvo anteriormente" : ""}
                        >
                            <IoIosDocument />
                            {saving ? 'Salvando...' : 'Salvar Parecer'}
                        </button>

                        {/* Feedback de parecer salvo - inline e verde */}
                        {savedReport && (
                            <div className="px-4 py-2 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center gap-2">
                                <span className="font-medium">Parecer salvo com sucesso!</span>
                                <span className="text-sm">Salvo em: {savedReport.savedAt}</span>
                            </div>
                        )}
                    </div>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                        onClick={clearFilters}
                    >
                        Limpar Filtros
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            {/* Resumo */}
            {documents.length > 0 && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Total Documentos</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {documents.length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Aprovados</div>
                        <div className="text-lg font-semibold text-green-600">
                            {documents.filter(d => d.finalRecommendation === 'APPROVED').length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Reprovados</div>
                        <div className="text-lg font-semibold text-red-600">
                            {documents.filter(d => d.finalRecommendation === 'REJECTED').length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Pendentes</div>
                        <div className="text-lg font-semibold text-yellow-600">
                            {documents.filter(d => !d.finalRecommendation).length}
                        </div>
                    </div>
                </div>
            )}

            {/* Tabela de Documentos */}
            {documents.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-slate-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Documento
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Nome
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Tipo
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        INOVAMIND
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        ESCAVADOR
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        QUOD
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Recomendação Final
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Nível de Risco
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Sinalizar Crédito
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Observações
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Regras
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {documents.map((doc, index) => {
                                    // Borda esquerda = Recomendação Final
                                    const recommendationBorderColor = doc.finalRecommendation === 'APPROVED' ? 'border-l-green-500' :
                                                                     doc.finalRecommendation === 'REJECTED' ? 'border-l-red-500' : 
                                                                     'border-l-gray-300';
                                    
                                    // Background = Nível de Risco
                                    const riskBackgroundColor = doc.riskLevel === 'BAIXO' ? 'bg-green-50 dark:bg-green-900/20' :
                                                               doc.riskLevel === 'MEDIO' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                                                               doc.riskLevel === 'ALTO' ? 'bg-red-50 dark:bg-red-900/20' : '';
                                    
                                    return (
                                    <tr key={index} className={`hover:bg-gray-100 dark:hover:bg-slate-600 border-l-4 ${recommendationBorderColor} ${riskBackgroundColor}`}>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {doc.document}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                                            <div className="truncate" title={doc.entityName}>
                                                {doc.entityName || '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {doc.entityType === 'CPF' ? 'PF' : 'PJ'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.INOVAMIND ? getRecommendationBadge(doc.providers.INOVAMIND.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.ESCAVADOR ? getRecommendationBadge(doc.providers.ESCAVADOR.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.QUOD ? getRecommendationBadge(doc.providers.QUOD.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <SelectFilter
                                                id={`final-recommendation-${index}`}
                                                label=""
                                                value={doc.finalRecommendation}
                                                onChange={(value) => updateFinalRecommendation(index, value as any)}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione..."
                                                options={[
                                                    { value: 'APPROVED', label: 'Aprovado' },
                                                    { value: 'REJECTED', label: 'Reprovado' }
                                                ]}
                                                className="w-full"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <SelectFilter
                                                id={`risk-level-${index}`}
                                                label=""
                                                value={doc.riskLevel || ''}
                                                onChange={(value) => updateRiskLevel(index, value as any)}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione..."
                                                options={[
                                                    { value: 'BAIXO', label: 'Baixo' },
                                                    { value: 'MEDIO', label: 'Médio' },
                                                    { value: 'ALTO', label: 'Alto' }
                                                ]}
                                                className="w-full"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                    checked={doc.creditFlag ?? false}
                                                    onChange={(e) => updateCreditFlag(index, e.target.checked)}
                                                />
                                            </div>
                                        </td>

                                        <td className="px-4 py-3">
                                            <textarea
                                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-gray-600 dark:text-white"
                                                placeholder="Observações..."
                                                value={doc.observations}
                                                onChange={(e) => updateObservations(index, e.target.value)}
                                                rows={3}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm flex items-center justify-center p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                                                onClick={() => showRules(doc)}
                                                title="Ver Regras"
                                            >
                                                <IoMdEye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal de Regras */}
            {showRulesModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Regras Acionadas
                        </h3>

                        {selectedDocumentRules.length > 0 ? (
                            <ul className="space-y-2">
                                {selectedDocumentRules.map((rule, index) => (
                                    <li key={index} className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-slate-700 rounded">
                                        {rule}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400">Nenhuma regra acionada</p>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                onClick={() => setShowRulesModal(false)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageForm>
    );
};

export default RecommendationEngineParecerView;
